<script lang="ts">
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import FuturePrices from "@retailer/svelte/ProductDetails/Components/FuturePrices.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Price from "@retailer/svelte/Shared/Price.svelte";
  import PostOffPricing from "@retailer/svelte/ProductDetails/Components/PostOffPricing.svelte";
  import { ProductLineListingStore } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { showDealsToUser } from "@retailer/services/product_card_service";
  import {
    distributorIsSevenFiftyIntegrated,
    showFuturePricing,
    showPostOffPricing,
  } from "@retailer/services/product_card_service";

  import type { DualProductDistributor } from "@shared/types/Distributor";
  import type { Product, ProductLine } from "@shared/types/ProductLine";
  import { onDestroy, onMount } from "svelte";

  const { trackClick } = EventTracking;

  export let productLine: ProductLine;
  export let product: Product;

  let priceScheduleAvailable: boolean = false;
  let postOffPricingAvailable: boolean = false;
  let futurePricesData: any = null;
  let hasDeal: boolean = false;

  const canShowPrices = !$CurrentUserStore.limited_ordering;

  const productLineDualDistributors: DualProductDistributor[] =
    productLine.dual_product_distributors || [];
  const matchingDualDistributor = productLineDualDistributors.find(
    d => d.id == product.distributor_id,
  );

  const sevenFiftyIntegratedDistributor: boolean = distributorIsSevenFiftyIntegrated(
    matchingDualDistributor,
    productLine.distributor_info,
  );

  const unsubscribe = ProductLineListingStore.subscribe(n => {
    const discounts = n.discountData[productLine.id] || [];
    const hasDiscount = discounts.some(p => p?.inventory_id === product.inventory_id);
    hasDeal = showDealsToUser() && hasDiscount;
    futurePricesData = n.productLineFuturePrices[productLine.id]?.filter(
      p => p?.inventory_id == product.inventory_id,
    );
    priceScheduleAvailable =
      !!futurePricesData?.length && showFuturePricing(sevenFiftyIntegratedDistributor);
    postOffPricingAvailable = showPostOffPricing(sevenFiftyIntegratedDistributor);
  });

  onMount(async () => {
    await InventoryItemStore.fetchInventoryItem(product.inventory_id);
  });

  onDestroy(() => {
    unsubscribe();
  });

  $: cartWidgetItem = $InventoryItemStore?.inventoryItems[product.inventory_id];
  $: cartWidgetItemSoldBy = cartWidgetItem?.sold_by;
  $: distributorUsesHappyHour = cartWidgetItem?.distributor_summary.uses_happy_hour;
  $: hasHappyHourPrices = cartWidgetItem?.has_hh_prices;
  $: showPrices =
    canShowPrices &&
    (!distributorUsesHappyHour || (distributorUsesHappyHour && hasHappyHourPrices));

  const openFuturePricesTargetObjectType = "product_card_sku_item_open_future_prices_modal";
  const closeFuturePricesTargetObjectType = "product_card_sku_item_close_future_prices_modal";

  // This is needed for the min bottle future pricing modal to work
  const futurePricesProductLine = {
    ...productLine,
    distributor: {
      ...product?.distributor_info,
    },
  };
</script>

<TrackingContext let:trackingContext>
  <!-- price and deal info -->
  <div class="fizz-flex-vertical">
    {#if hasDeal}
      <div class="fizz-flex-row-4" data-testid="sku-item-deal-tag">
        <Icon type="Deal" height="12" width="12" extraClassNames="fizz-text-deal" />

        <span class="fizz-text-subdued"> Deal </span>
      </div>
    {/if}

    {#if showPrices}
      {#if postOffPricingAvailable}
        <PostOffPricing
          {product}
          {productLine}
          bind:futurePricesData
          flexVertical={true}
          showPricePerOunce={false}
          showPriceNotAvailable={true}
        />
      {:else}
        <Price
          {product}
          {productLine}
          flexVertical={true}
          showPriceNotAvailable={true}
          showPricePerOunce={false}
        />
      {/if}

      {#if priceScheduleAvailable}
        <div data-testid="sku-item-price-schedule" id="sku-item-price-schedule">
          <FuturePrices
            {product}
            productLine={futurePricesProductLine}
            {futurePricesData}
            justifyEnd={false}
            onOpenModal={() =>
              trackClick(openFuturePricesTargetObjectType, trackingContext.entities, false)}
            onCloseModal={() =>
              trackClick(closeFuturePricesTargetObjectType, trackingContext.entities, false)}
          />
        </div>
      {/if}
    {:else}
      <div data-testid="sku-item-price-not-available">Price not available</div>
    {/if}
  </div>
</TrackingContext>
